/**
 * Score Display Normalization
 *
 * Maps raw scoring engine scores (which can be negative) to a user-friendly
 * 0-100 display scale. Raw scores are preserved in DB and API responses;
 * this module is used only for user-facing display.
 *
 * Normalization formula:
 *   slope = 40 / (betThreshold - skipThreshold)
 *   intercept = 75 - slope * betThreshold
 *   displayScore = clamp(slope * rawScore + intercept, 0, 100)
 *
 * For XGBoost thresholds (bet=9, skip=-3): slope=3.33, intercept=45
 *   Raw -14 -> display 0, Raw -3 -> 35 (SKIP boundary), Raw 7 -> 68 (LEAN BET), Raw 9 -> 75 (BET boundary), Raw 17 -> 100
 */

import { shortStatLabel } from './statLabels';

// =============================================================================
// Constants
// =============================================================================

/** Fixed display thresholds for the 0-100 scale */
export const DISPLAY_THRESHOLDS = {
  BET: 75,
  LEAN_BET: 67,
  CAUTION: 35,
} as const;

/**
 * DEFAULT_RAW_THRESHOLDS — intentional last-resort fallback when the
 * XGBoost model artifact is missing or fails to load. Values are stale
 * (set 2026-03-28 for the v8.0 model) but only reachable in a degraded
 * state. Per user principle 2026-05-28 we preserve the constant to keep
 * the fallback path explicit rather than letting normalizeScoreForDisplay
 * throw on undefined thresholds.
 *
 * The active path (model loaded, post-section-03) reads per-prop
 * thresholds via getXGBoostThresholds(propType?) — these constants are
 * only consulted when both the universal artifact and per-prop manifest
 * are unavailable.
 */
export const DEFAULT_RAW_THRESHOLDS = {
  bet: 4,
  skip: -6,
} as const;

/**
 * Legacy per-extra-leg threshold scaling (only used when the non-calibrated path is active).
 * The calibrated path uses flat thresholds regardless of leg count.
 */
export const THRESHOLD_PER_EXTRA_LEG = {
  bet: 8,
  skip: 5,
} as const;

/** Display recommendation labels */
export type DisplayScoreRecommendation = 'BET' | 'LEAN BET' | 'CAUTION' | 'SKIP';

/** Display color semantic tokens */
export type DisplayScoreColor = 'success' | 'lean' | 'caution' | 'danger';

// =============================================================================
// Functions
// =============================================================================

/** Legacy base thresholds for the non-calibrated scoring path */
export const LEGACY_RAW_THRESHOLDS = {
  bet: 20,
  skip: -5,
} as const;

/**
 * Get raw scoring thresholds for a given leg count.
 * Only needed for the legacy (non-calibrated) scoring path.
 * The calibrated path uses flat thresholds (DEFAULT_RAW_THRESHOLDS).
 */
export function getThresholdsForLegCount(legCount: number): { bet: number; skip: number } {
  const extraLegs = Math.max(0, legCount - 2);
  return {
    bet: LEGACY_RAW_THRESHOLDS.bet + extraLegs * THRESHOLD_PER_EXTRA_LEG.bet,
    skip: LEGACY_RAW_THRESHOLDS.skip - extraLegs * THRESHOLD_PER_EXTRA_LEG.skip,
  };
}

/**
 * Normalize a raw score to the 0-100 display scale.
 *
 * @param rawScore - The raw score from the scoring engine
 * @param thresholds - Optional raw thresholds { bet, skip }. Defaults to 2-leg thresholds.
 * @returns A number between 0 and 100
 */
export function normalizeScoreForDisplay(
  rawScore: number,
  thresholds?: { bet: number; skip: number },
): number {
  const { bet, skip } = thresholds ?? DEFAULT_RAW_THRESHOLDS;
  if (bet === skip) return 50; // Guard against division by zero
  const slope = 40 / (bet - skip);
  const intercept = DISPLAY_THRESHOLDS.BET - slope * bet;
  const display = slope * rawScore + intercept;
  return Math.round(Math.max(0, Math.min(100, display)));
}

/**
 * Derive a display recommendation from a normalized (0-100) score.
 * These are fixed thresholds that never change, making them intuitive for users.
 */
export function getDisplayRecommendation(displayScore: number): DisplayScoreRecommendation {
  if (displayScore >= DISPLAY_THRESHOLDS.BET) return 'BET';
  if (displayScore >= DISPLAY_THRESHOLDS.LEAN_BET) return 'LEAN BET';
  if (displayScore >= DISPLAY_THRESHOLDS.CAUTION) return 'CAUTION';
  return 'SKIP';
}

/**
 * Get a semantic color key for a display score.
 * Used to map to theme tokens (success, warning, danger, etc.).
 */
export function getDisplayColor(displayScore: number): DisplayScoreColor {
  if (displayScore >= DISPLAY_THRESHOLDS.BET) return 'success';
  if (displayScore >= DISPLAY_THRESHOLDS.LEAN_BET) return 'lean';
  if (displayScore >= DISPLAY_THRESHOLDS.CAUTION) return 'caution';
  return 'danger';
}

export type ParlayLabel = DisplayScoreRecommendation | 'NO SIGNAL';
/** `none` is NO SIGNAL's: no call, which must not be coloured as an adverse one. */
export type ParlayLabelColor = DisplayScoreColor | 'none';

/** BET and LEAN BET read NO SIGNAL when `isLabelTrusted` is false; absent or null keeps them. */
export function getParlayLabel(
  displayScore: number,
  isLabelTrusted: boolean | null | undefined,
): { label: ParlayLabel; color: ParlayLabelColor } {
  const label = getDisplayRecommendation(displayScore);
  if (isLabelTrusted === false && (label === 'BET' || label === 'LEAN BET')) {
    return { label: 'NO SIGNAL', color: 'none' };
  }
  return { label, color: getDisplayColor(displayScore) };
}

/** A parlay score's display score, label and colour, from its own thresholds and trust flag. */
export function getNormalizedFromParlayScore(score: {
  score: number;
  thresholds?: { bet: number; skip: number };
  isLabelTrusted?: boolean;
}): { displayScore: number; displayRec: ParlayLabel; displayColor: ParlayLabelColor } {
  const thresholds = score.thresholds
    ? { bet: score.thresholds.bet, skip: score.thresholds.skip }
    : undefined;
  const displayScore = normalizeScoreForDisplay(score.score, thresholds);
  const { label, color } = getParlayLabel(displayScore, score.isLabelTrusted);
  return { displayScore, displayRec: label, displayColor: color };
}

// =============================================================================
// Factor Normalization — makes individual factors sum to the display score
// =============================================================================

/** Minimal factor shape accepted by the normalizer (avoids coupling to server types) */
interface FactorInput {
  factor: string;
  impact: 'positive' | 'negative' | 'neutral';
  score: number;
  winRate?: number;
  profit?: number;
  sampleSize?: number;
  details?: string;
  legIndex?: number;
  playerName?: string;
  propType?: string;
  isWarning?: boolean;
  confidence?: 'high' | 'low';
}

/** A scoring factor extended with its display-scale score */
export interface DisplayFactor extends FactorInput {
  displayScore: number;
}

/** A scoring leg's player and stat in the stat's short form: "Obi Toppin PTS+REB". */
export function legGroupLabel(playerName: string, propType?: string): string {
  return propType ? `${playerName} ${shortStatLabel(propType)}` : playerName;
}

/** Result of normalizing factors for display */
export interface NormalizedFactorBreakdown {
  /** The intercept / baseline on the 0-100 scale (e.g. 67) */
  baseline: number;
  /** Each factor with a displayScore that uses the same scale as the total */
  factors: DisplayFactor[];
  /** Residual shown as "Other factors" when |residual| > 2 */
  baseAnalysis: number | null;
  /** The final clamped 0-100 display score */
  displayTotal: number;
}

/**
 * Redistribute a small residual (|residual| <= 2) across factors by adjusting
 * the highest-magnitude factors by ±1 until the residual is zero.
 */
function redistributeResidual(factors: DisplayFactor[], residual: number): void {
  if (residual === 0 || factors.length === 0) return;

  // Sort by descending absolute displayScore so we adjust the biggest factors
  const sorted = [...factors]
    .map((f, i) => ({ idx: i, abs: Math.abs(f.displayScore) }))
    .sort((a, b) => b.abs - a.abs);

  let remaining = residual;
  const step = remaining > 0 ? 1 : -1;

  for (const { idx } of sorted) {
    if (remaining === 0) break;
    // Only adjust non-neutral factors
    if (factors[idx].impact !== 'neutral') {
      factors[idx].displayScore += step;
      remaining -= step;
    }
  }
}

/**
 * Transform raw scoring factors into display-scale factors that sum to the
 * displayed total score.
 *
 * Guarantees: baseline + sum(displayScores) + (baseAnalysis ?? 0) === displayTotal
 *
 * @param rawScore   - The raw score from the scoring engine
 * @param factors    - Array of raw scoring factors
 * @param thresholds - Optional raw thresholds { bet, skip }
 */
export function normalizeFactorsForDisplay(
  rawScore: number,
  factors: FactorInput[],
  thresholds?: { bet: number; skip: number },
): NormalizedFactorBreakdown {
  const { bet, skip } = thresholds ?? DEFAULT_RAW_THRESHOLDS;
  const slope = bet === skip ? 1 : 40 / (bet - skip);
  const intercept = bet === skip ? 50 : DISPLAY_THRESHOLDS.BET - slope * bet;
  const displayTotal = normalizeScoreForDisplay(rawScore, thresholds);
  const baseline = Math.round(intercept);

  // Filter to non-neutral factors for scoring math
  const nonNeutralFactors = factors.filter((f) => f.impact !== 'neutral');
  const neutralFactors = factors.filter((f) => f.impact === 'neutral');

  if (nonNeutralFactors.length === 0) {
    // No scored factors — everything is baseline or residual
    const allDisplay: DisplayFactor[] = factors.map((f) => ({
      ...f,
      displayScore: 0,
    }));
    const residual = displayTotal - baseline;
    return {
      baseline,
      factors: allDisplay,
      baseAnalysis: residual !== 0 ? residual : null,
      displayTotal,
    };
  }

  // Scale each non-neutral factor by the normalization slope
  const scaledNonNeutral: DisplayFactor[] = nonNeutralFactors.map((f) => ({
    ...f,
    displayScore: Math.round(slope * f.score),
  }));

  const scaledSum = scaledNonNeutral.reduce((sum, f) => sum + f.displayScore, 0);
  const residual = displayTotal - baseline - scaledSum;

  let baseAnalysis: number | null = null;

  if (Math.abs(residual) <= 2) {
    // Small rounding gap — redistribute invisibly
    redistributeResidual(scaledNonNeutral, residual);
  } else {
    // Larger gap (from clamping or model base analysis) — show explicitly
    baseAnalysis = residual;
  }

  // Merge neutral factors back in (displayScore = 0) preserving original order
  const factorMap = new Map<FactorInput, DisplayFactor>();
  for (const [i, f] of scaledNonNeutral.entries()) factorMap.set(nonNeutralFactors[i], f);
  for (const f of neutralFactors) factorMap.set(f, { ...f, displayScore: 0 });

  const allDisplay: DisplayFactor[] = factors.map(
    (f) => factorMap.get(f) ?? { ...f, displayScore: 0 },
  );

  return {
    baseline,
    factors: allDisplay,
    baseAnalysis,
    displayTotal,
  };
}
